import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, ChevronLeft, ChevronRight, LogOut, X } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { logoutApi } from '@/lib/api/client';
import { useAuthStore } from '@/shared/store/auth.store';
import {
  useAceptarTerminosMutation,
  useActualizarProgresoMutation,
  useCompletarOnboardingMutation,
} from '@/features/onboarding/queries/onboarding.queries';
import { OnboardingContentViewer } from './onboarding-content-viewer';
import type { OnboardingUsuarioDto } from '@/features/onboarding/types/onboarding.types';

interface OnboardingWizardProps {
  data: OnboardingUsuarioDto;
  isTutorial: boolean;
  onComplete: () => void;
}

export function OnboardingWizard({ data, isTutorial, onComplete }: OnboardingWizardProps) {
  const navigate = useNavigate();
  const user = useAuthStore(s => s.user);
  const [currentStep, setCurrentStep] = useState(Math.max(1, data.pasoActual));
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [aceptoTerminos, setAceptoTerminos] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const completarMutation = useCompletarOnboardingMutation();
  const progresoMutation = useActualizarProgresoMutation();
  const aceptarTerminosMutation = useAceptarTerminosMutation();

  const totalSteps = data.pasos.length;
  const currentPaso = data.pasos[currentStep - 1];
  const isLastStep = currentStep === totalSteps;

  // Check scroll position
  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 50;
    if (atBottom) setHasScrolledToBottom(true);
  }, []);

  // Reset scroll-state flag when the step changes — store the previous step
  // in state and compare during render so we do not call setState inside an
  // effect body (React 19's recommended pattern).
  const [prevStep, setPrevStep] = useState(currentStep);
  if (currentStep !== prevStep) {
    setPrevStep(currentStep);
    setHasScrolledToBottom(false);
  }

  // Scroll the content container back to top on every step change. DOM work
  // and the follow-up check stay in an effect since they touch a ref.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
    requestAnimationFrame(() => checkScroll());
  }, [currentStep, checkScroll]);

  // Attach scroll listener
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkScroll);
    return () => el.removeEventListener('scroll', checkScroll);
  }, [checkScroll]);

  const goNext = useCallback(() => {
    if (isLastStep) {
      const finalize = () =>
        completarMutation.mutate(undefined, {
          onSuccess: () => {
            toast.success('Onboarding completado');
            onComplete();
          },
        });
      // En primera vez se exige aceptar terminos y se persiste. En tutorial
      // ya fue aceptado previamente, asi que se omite.
      if (!isTutorial) {
        aceptarTerminosMutation.mutate('1.0', { onSuccess: finalize });
        return;
      }
      finalize();
      return;
    }
    setCompletedSteps(prev => new Set([...prev, currentStep]));
    const next = currentStep + 1;
    setCurrentStep(next);
    progresoMutation.mutate(next);
  }, [
    currentStep,
    isLastStep,
    isTutorial,
    completarMutation,
    progresoMutation,
    aceptarTerminosMutation,
    onComplete,
  ]);

  const goPrev = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const canProceed =
    (hasScrolledToBottom || isTutorial) && (!isLastStep || isTutorial || aceptoTerminos);

  return (
    <div className="bg-background fixed inset-0 z-50 flex flex-col">
      {/* Main area with border */}
      <div className="m-6 flex flex-1 overflow-hidden rounded-lg border">
        {/* Inner header */}
        <div className="flex flex-1 flex-col">
          <div className="flex items-center justify-between border-b px-6 py-3">
            <span className="text-sm font-semibold">RECET</span>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-sm">
                Bienvenido, {user?.nombreCorto || user?.nombres || 'Usuario'} &mdash;{' '}
                {isTutorial ? 'Tutorial' : 'Primera vez'}
              </span>
              {isTutorial && (
                <Button variant="ghost" size="sm" onClick={onComplete} className="ml-2">
                  <X className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive ml-2"
                onClick={async () => {
                  await logoutApi();
                  navigate({ to: '/login' });
                }}
              >
                <LogOut className="mr-1 h-4 w-4" />
                Cerrar sesión
              </Button>
            </div>
          </div>

          {/* Content body */}
          <div className="flex flex-1 overflow-hidden">
            {/* Left sidebar - Step list */}
            <aside className="w-72 shrink-0 overflow-y-auto border-r py-4">
              <nav className="space-y-1 px-3">
                {data.pasos.map((paso, index) => {
                  const stepNum = index + 1;
                  const isActive = stepNum === currentStep;
                  const isCompleted = completedSteps.has(stepNum);
                  const canNavigate = isCompleted || stepNum <= currentStep;

                  return (
                    <button
                      key={paso.id}
                      type="button"
                      disabled={!canNavigate}
                      onClick={() => canNavigate && setCurrentStep(stepNum)}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-sm transition-colors',
                        canNavigate && !isActive && 'hover:bg-muted/50 cursor-pointer',
                        isActive && 'bg-muted font-medium',
                        !isActive && !isCompleted && 'text-muted-foreground',
                        isCompleted && !isActive && 'text-foreground',
                        !canNavigate && 'cursor-default'
                      )}
                    >
                      <span
                        className={cn(
                          'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs',
                          isActive && 'border-primary bg-primary/10 text-primary font-semibold',
                          isCompleted &&
                            !isActive &&
                            'border-primary bg-primary text-primary-foreground',
                          !isActive &&
                            !isCompleted &&
                            'border-muted-foreground/40 text-muted-foreground'
                        )}
                      >
                        {isCompleted && !isActive ? <Check className="h-3.5 w-3.5" /> : stepNum}
                      </span>
                      <span>{paso.titulo}</span>
                    </button>
                  );
                })}
              </nav>
            </aside>

            {/* Main content */}
            <div className="flex flex-1 flex-col">
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-8 py-6">
                <p className="text-muted-foreground mb-1 text-sm">
                  Paso {currentStep} de {totalSteps}
                </p>
                <h2 className="mb-6 text-2xl font-bold">{currentPaso?.titulo}</h2>

                {/* Content card */}
                <div className="rounded-lg border p-6">
                  {currentPaso && <OnboardingContentViewer content={currentPaso.contenido} />}
                </div>

                {/* Aceptacion de terminos en la ultima etapa (primera vez) */}
                {isLastStep && !isTutorial && (
                  <div className="mt-4 flex items-start gap-2 rounded-lg border p-4">
                    <Checkbox
                      id="acepto-terminos"
                      checked={aceptoTerminos}
                      onCheckedChange={checked => setAceptoTerminos(checked === true)}
                      className="border-2 border-orange-500"
                    />
                    <label htmlFor="acepto-terminos" className="text-sm leading-snug">
                      He leído y acepto los Términos de Uso, el Aviso Legal y la Política de
                      Privacidad.
                    </label>
                  </div>
                )}
              </div>

              {/* Footer navigation */}
              <footer className="flex items-center border-t px-8 py-3">
                <div className="w-32">
                  <Button variant="ghost" size="sm" onClick={goPrev} disabled={currentStep === 1}>
                    <ChevronLeft className="mr-1 h-4 w-4" />
                    Anterior
                  </Button>
                </div>

                <span className="text-muted-foreground flex-1 text-center text-sm italic">
                  Desplazate hasta el final para continuar
                </span>

                <div className="flex w-32 justify-end">
                  <Button
                    size="sm"
                    onClick={goNext}
                    disabled={!canProceed || completarMutation.isPending}
                  >
                    {isLastStep ? 'Finalizar' : 'Siguiente'}
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </footer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
